import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { IconButton, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { PagedData_1OfWasteHeaders, WasteHeaders } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { formatDateHourString } from '../../../utils';
import { Trash01 } from '../../../assets';
import { SetStateAction, Dispatch } from 'react';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { WasteChecksheetTab } from '../enums/WasteChecksheetTab';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { TableHeader } from '../../../components/forms/TableHeader';

export default function WasteChecksheetTable(props: {
  readonly list: PagedData_1OfWasteHeaders,
  readonly activeTab: WasteChecksheetTab;
  readonly currencyCode?: string,
  readonly page: number,
  readonly setPage: Dispatch<SetStateAction<number>>,
  readonly order: 'asc' | 'desc',
  readonly setOrder: Dispatch<SetStateAction<'asc' | 'desc'>>,
  readonly orderBy: string,
  readonly setOrderBy: Dispatch<SetStateAction<string>>,
  readonly date: string,
  readonly clearAll: () => void;
  readonly setDeleteId: Dispatch<SetStateAction<number>>;
  readonly setOpenModal: Dispatch<SetStateAction<boolean>>;
  readonly openModal: boolean;
  readonly handleConfirmDeleteModal: () => Promise<void>;
  readonly isLoading: boolean;
  readonly navigateToDailyWasteCheckSheet: () => void;
  readonly isCreating: boolean;
  readonly onAddIsDisabled: boolean;
}) {
  const { t } = useTranslation('wasteChecksheet');
  const navigate = useNavigate();
  const theme = useTheme();

  const rowsPerPage = 10;
  const maxPages = props.list?.totalCount
    ? Math.max(1, Math.ceil(props.list.totalCount / rowsPerPage))
    : 0;

  //Header sort
  const handleSort = (property: keyof WasteHeaders) => {
    const isAsc = props.orderBy === property && props.order === 'asc';
    props.setPage(0);
    props.setOrder(isAsc ? 'desc' : 'asc');
    props.setOrderBy(property);
  };

  const deleteButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    props.setDeleteId(id);
    props.setOpenModal(true);
  };

  const handleCancelModal = () => {
    props.setOpenModal(false);
  };

  if (props.list.data?.length == 0) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('title')}
      searchTerm={props.date}
      onClear={props.clearAll}
      onAdd={props.navigateToDailyWasteCheckSheet}
      onAddIsCreating={props.isCreating}
      addItemText={t('createNewBtn')}
      onAddIsDisabled={props.onAddIsDisabled} />;
  }

  return (
    <>
      <TableHeader>
        {t('title')}
        {props.list?.totalCount && <Pill text={`${props.list?.totalCount} Checksheets`}></Pill>}
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={props.activeTab === WasteChecksheetTab.ARCHIVED ? 700 : 480} /> :
        <TableContainer>
          <Table sx={{ minWidth: props.activeTab === WasteChecksheetTab.ARCHIVED ? '700px' : '480px' }}>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('list.id')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'createdDate'}
                    direction={props.order}
                    onClick={() => handleSort('createdDate')}
                  >
                    {t('list.createdDate')}
                  </TableSortLabel>
                </TableCell>
                {
                  props.activeTab === WasteChecksheetTab.ARCHIVED &&
                  <>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={props.orderBy === 'submittedDate'}
                        direction={props.order}
                        onClick={() => handleSort('submittedDate')}
                      >
                        {t('list.submittedDate')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={props.orderBy === 'submittedBy'}
                        direction={props.order}
                        onClick={() => handleSort('submittedBy')}
                      >
                        {t('list.submittedBy')}
                      </TableSortLabel>
                    </TableCell></>
                }
                <TableCell sx={{ whiteSpace: 'nowrap' }}
                  align="right">
                  <TableSortLabel
                    active={props.orderBy === 'totalWaste'}
                    direction={props.order}
                    onClick={() => handleSort('totalWaste')}
                  >
                    {t('list.totalWaste')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}
                  align='right'>
                  <TableSortLabel
                    active={props.orderBy === 'totalCost'}
                    direction={props.order}
                    onClick={() => handleSort('totalCost')}
                  >
                    {t('list.totalCost')}
                  </TableSortLabel>
                </TableCell>
                {props.activeTab === WasteChecksheetTab.ACTIVE &&
                  <TableCell align="center">
                  </TableCell>
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list.data?.map((row) => (
                <TableRow key={row.id}
                  onClick={() => navigate(`/store/daily-waste-checksheet/${row.id}`)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.id ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.createdDate ? formatDateHourString(row.createdDate) : ''}
                    </Typography>
                  </TableCell>
                  {
                    props.activeTab === WasteChecksheetTab.ARCHIVED && (
                      <>
                        <TableCell>
                          {row.submittedDate ? formatDateHourString(row.submittedDate) : ''}
                        </TableCell>
                        <TableCell>
                          {row.submittedBy}
                        </TableCell>
                      </>
                    )
                  }
                  <TableCell align="right">
                    {row.totalWaste}
                  </TableCell>
                  <TableCell align="right">
                    <RoundAndFormatValue value={row.totalCost ?? 0}
                      currencyCode={props.currencyCode} />
                  </TableCell>
                  {props.activeTab === WasteChecksheetTab.ACTIVE &&
                    <TableCell
                      align="center"
                      sx={{ p: 0, alignContent: 'center' }}
                    >
                      <IconButton
                        size='small'
                        onClick={(event) => deleteButtonHandler(event, row.id ?? -1)}>
                        <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                      </IconButton>
                    </TableCell>}
                </TableRow>
              ))}

              <EmptyRows numberOfCurrentRows={props.list.data?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={props.isLoading} />
      <ConfirmationDeleteDialog open={props.openModal}
        onCancel={handleCancelModal}
        onConfirm={props.handleConfirmDeleteModal}
        nameOfObject={t('details.title')} />
    </>
  );
}


